import { useEffect, useState, type CSSProperties } from "react";
import {
  findDuplicates,
  searchMergeEntities,
  compareMergeEntities,
  executeMerge,
  getMergeHistoryList,
  type DuplicatePair,
  type MergeCompareResult,
  type MergeHistoryEntry,
} from "../../lib/api";
import WhiskeyStudioTabs from "../../components/admin/WhiskeyStudioTabs";
import { customerFieldOverrides, defaultCustomerPrimary, defaultSquareIdSide } from "./mergeCustomerDefaults";

type EntityType = "bottles" | "distilleries" | "customers";

interface MergeField {
  key: string;
  label: string;
  isMoney?: boolean;
}

const BOTTLE_FIELDS: MergeField[] = [
  { key: "name", label: "Bottle Name" },
  { key: "category_slug", label: "Category" },
  { key: "distillery", label: "Distillery" },
  { key: "proof", label: "Proof" },
  { key: "age_years", label: "Age (Years)" },
  { key: "region", label: "Region" },
  { key: "country", label: "Country" },
  { key: "price_cents", label: "Price", isMoney: true },
  { key: "upc", label: "UPC" },
  { key: "description", label: "Description" },
];

const DISTILLERY_FIELDS: MergeField[] = [
  { key: "name", label: "Distillery Name" },
  { key: "region", label: "Region" },
  { key: "state", label: "State" },
  { key: "country", label: "Country" },
  { key: "website_url", label: "Website" },
  { key: "description", label: "Description" },
];

const CUSTOMER_FIELDS: MergeField[] = [
  { key: "first_name", label: "First Name" },
  { key: "last_name", label: "Last Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "square_customer_id", label: "Square ID" },
  { key: "square_updated_at", label: "Square last updated" },
  // Consent is not chosen here: merge-customers moves it WITH the email/phone it belongs to.
  { key: "email_opt_in", label: "Email opt-in (moves with email)" },
  { key: "sms_opt_in", label: "SMS opt-in (moves with phone)" },
];

export default function AdminMerge() {
  const [entityType, setEntityType] = useState<EntityType>("bottles");
  const [tab, setTab] = useState<"find" | "history">("find");

  // Duplicates State
  const [pairs, setPairs] = useState<DuplicatePair[]>([]);
  const [loadingPairs, setLoadingPairs] = useState(false);

  // Search State for manual merge
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [manualItemA, setManualItemA] = useState<any | null>(null);

  // Compare & Merge Modal State
  const [compareModal, setCompareModal] = useState<MergeCompareResult | null>(null);
  const [primaryChoice, setPrimaryChoice] = useState<"a" | "b">("a");
  const [fieldOverrides, setFieldOverrides] = useState<Record<string, any>>({});
  const [squareIdSide, setSquareIdSide] = useState<"a" | "b" | null>(null);
  const [merging, setMerging] = useState(false);

  // History State
  const [history, setHistory] = useState<MergeHistoryEntry[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Messages
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 4000);
  };

  const loadPairs = async () => {
    setLoadingPairs(true);
    setErrorMsg(null);
    try {
      const res = await findDuplicates(entityType);
      setPairs(res.pairs || []);
    } catch (err: any) {
      setErrorMsg("Failed to scan duplicates: " + (err?.message || "Error"));
    } finally {
      setLoadingPairs(false);
    }
  };

  const loadHistory = async () => {
    setLoadingHistory(true);
    setErrorMsg(null);
    try {
      const res = await getMergeHistoryList(entityType);
      setHistory(res.history || []);
    } catch (err: any) {
      setErrorMsg("Failed to load merge history: " + (err?.message || "Error"));
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    setManualItemA(null);
    setSearchResults([]);
    setSearchQuery("");
    if (tab === "find") loadPairs();
    else loadHistory();
  }, [entityType, tab]);

  // Search Debounce
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await searchMergeEntities(entityType, searchQuery.trim());
        const list = res.items || res.distilleries || res.customers || [];
        setSearchResults(list);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [searchQuery, entityType]);

  const handleOpenCompare = async (itemA: any, itemB: any) => {
    setErrorMsg(null);
    try {
      const comp = await compareMergeEntities(entityType, itemA.id, itemB.id);
      setCompareModal(comp);
      setFieldOverrides({});
      if (entityType === "customers") {
        setPrimaryChoice(defaultCustomerPrimary(comp.item_a, comp.item_b));
        setSquareIdSide(defaultSquareIdSide(comp.item_a, comp.item_b));
      } else {
        setPrimaryChoice("a");
        setSquareIdSide(null);
      }
    } catch (err: any) {
      setErrorMsg("Failed to compare items: " + (err?.message || "Error"));
    }
  };

  const handleSelectManual = (item: any) => {
    if (!manualItemA) {
      setManualItemA(item);
      setSearchQuery("");
      setSearchResults([]);
      showToast(`Selected first item: "${getItemName(item)}". Now search and select the second item.`);
    } else {
      if (manualItemA.id === item.id) {
        alert("Cannot merge an item with itself.");
        return;
      }
      handleOpenCompare(manualItemA, item);
      setManualItemA(null);
      setSearchQuery("");
      setSearchResults([]);
    }
  };

  const handleExecuteMerge = async () => {
    if (!compareModal) return;
    setMerging(true);
    try {
      const primary = primaryChoice === "a" ? compareModal.item_a : compareModal.item_b;
      const secondary = primaryChoice === "a" ? compareModal.item_b : compareModal.item_a;

      const overrides =
        entityType === "customers"
          ? customerFieldOverrides(primaryChoice, squareIdSide)
          : Object.keys(fieldOverrides).length > 0
          ? fieldOverrides
          : undefined;
      await executeMerge(entityType, {
        primary_id: primary.id,
        secondary_id: secondary.id,
        field_overrides: overrides,
      });

      setCompareModal(null);
      showToast(`Successfully merged "${getItemName(secondary)}" into "${getItemName(primary)}".`);
      await loadPairs();
    } catch (err: any) {
      alert("Merge failed: " + (err?.message || "Error"));
    } finally {
      setMerging(false);
    }
  };

  const getItemName = (item: any) => {
    if (!item) return "Unknown";
    if (entityType === "customers") {
      return (
        [item.first_name, item.last_name].filter(Boolean).join(" ") ||
        item.email ||
        item.phone ||
        "Unnamed Customer"
      );
    }
    return item.name || item.title || "Unnamed";
  };

  const getItemSub = (item: any) => {
    if (!item) return "";
    if (entityType === "customers") {
      return [item.email, item.phone].filter(Boolean).join(" · ");
    }
    if (entityType === "distilleries") {
      return [item.region, item.state, item.country].filter(Boolean).join(", ");
    }
    return [item.category_slug, item.distillery, item.proof ? `${item.proof} proof` : null]
      .filter(Boolean)
      .join(" · ");
  };

  const getFieldsList = () => {
    if (entityType === "customers") return CUSTOMER_FIELDS;
    if (entityType === "distilleries") return DISTILLERY_FIELDS;
    return BOTTLE_FIELDS;
  };

  return (
    <div style={styles.container}>
      <WhiskeyStudioTabs activeTab="duplicates" badgeCounts={{ duplicates: pairs.length }} />
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Merge Duplicates</h1>
          <p style={styles.subtitle}>
            Detect, compare, and resolve duplicate bottles, distilleries, and customer profiles
          </p>
        </div>
        <div style={styles.entityToggle}>
          <button
            style={{
              ...styles.entityBtn,
              ...(entityType === "bottles" ? styles.entityBtnActive : {}),
            }}
            onClick={() => setEntityType("bottles")}
          >
            🥃 Bottles
          </button>
          <button
            style={{
              ...styles.entityBtn,
              ...(entityType === "distilleries" ? styles.entityBtnActive : {}),
            }}
            onClick={() => setEntityType("distilleries")}
          >
            🏭 Distilleries
          </button>
          <button
            style={{
              ...styles.entityBtn,
              ...(entityType === "customers" ? styles.entityBtnActive : {}),
            }}
            onClick={() => setEntityType("customers")}
          >
            👥 Customers
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabsRow}>
        <div style={styles.tabToggle}>
          <button
            style={{
              ...styles.tabBtn,
              ...(tab === "find" ? styles.tabBtnActive : {}),
            }}
            onClick={() => setTab("find")}
          >
            Find Duplicates
          </button>
          <button
            style={{
              ...styles.tabBtn,
              ...(tab === "history" ? styles.tabBtnActive : {}),
            }}
            onClick={() => setTab("history")}
          >
            Merge History
          </button>
        </div>
        <button style={styles.refreshBtn} onClick={() => (tab === "find" ? loadPairs() : loadHistory())}>
          ↻ Refresh
        </button>
      </div>

      {/* Messages */}
      {errorMsg && <div style={styles.errorBanner}>{errorMsg}</div>}
      {successMsg && <div style={styles.successBanner}>{successMsg}</div>}

      {/* ═══ FIND DUPLICATES TAB ═══ */}
      {tab === "find" && (
        <div>
          {/* Manual Search Bar */}
          <div style={styles.searchCard}>
            <div style={styles.searchHeader}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "var(--gold, #F9AF36)" }}>
                {manualItemA ? (
                  <>
                    Step 2: Pick second item to merge with{" "}
                    <span style={{ color: "var(--text)" }}>"{getItemName(manualItemA)}"</span>
                  </>
                ) : (
                  "Manual Merge Search"
                )}
              </span>
              {manualItemA && (
                <button style={styles.cancelLink} onClick={() => setManualItemA(null)}>
                  Cancel Selection
                </button>
              )}
            </div>
            <input
              type="text"
              style={styles.searchInput}
              placeholder={
                entityType === "bottles"
                  ? "Search bottle by name, UPC, or category..."
                  : entityType === "distilleries"
                  ? "Search distillery by name or region..."
                  : "Search customer by name, email, or phone..."
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searching && <div style={styles.searchStatus}>Searching...</div>}
            {searchResults.length > 0 && (
              <div style={styles.resultsBox}>
                {searchResults.map((item) => (
                  <div
                    key={item.id}
                    style={styles.resultItem}
                    onClick={() => handleSelectManual(item)}
                  >
                    <div>
                      <div style={styles.resultName}>{getItemName(item)}</div>
                      <div style={styles.resultSub}>{getItemSub(item)}</div>
                    </div>
                    <button style={styles.selectBtn}>
                      {manualItemA ? "Merge With This →" : "Select First →"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Stats Bar */}
          <div style={styles.statsBar}>
            <div style={styles.statPill}>
              <span style={styles.statVal}>{pairs.length}</span>
              <span style={styles.statLabel}>Detected Pairs</span>
            </div>
            <div style={styles.statPill}>
              <span style={styles.statVal}>
                {pairs.filter((p) => p.confidence === "high" || p.match_type === "email_phone").length}
              </span>
              <span style={styles.statLabel}>High Confidence</span>
            </div>
          </div>

          {/* Pairs List */}
          {loadingPairs ? (
            <div style={styles.empty}>Scanning for duplicate {entityType}...</div>
          ) : pairs.length === 0 ? (
            <div style={styles.empty}>No duplicate {entityType} detected in catalog.</div>
          ) : (
            <div style={styles.pairsGrid}>
              {pairs.map((p, idx) => (
                <div key={idx} style={styles.pairCard}>
                  <div style={styles.pairTop}>
                    <span style={styles.matchBadge}>
                      {p.confidence ? `${p.confidence} confidence` : p.match_type || "duplicate match"}
                    </span>
                    {p.reason && <span style={styles.reasonText}>{p.reason}</span>}
                  </div>

                  <div style={styles.pairColumns}>
                    {/* Item A */}
                    <div style={styles.pairSide}>
                      <div style={styles.sideTag}>Item A</div>
                      <div style={styles.sideName}>{getItemName(p.item_a)}</div>
                      <div style={styles.sideMeta}>{getItemSub(p.item_a)}</div>
                    </div>

                    <div style={styles.vsBadge}>VS</div>

                    {/* Item B */}
                    <div style={styles.pairSide}>
                      <div style={styles.sideTag}>Item B</div>
                      <div style={styles.sideName}>{getItemName(p.item_b)}</div>
                      <div style={styles.sideMeta}>{getItemSub(p.item_b)}</div>
                    </div>
                  </div>

                  <div style={styles.pairActions}>
                    <button
                      style={styles.compareBtn}
                      onClick={() => handleOpenCompare(p.item_a, p.item_b)}
                    >
                      Compare & Merge →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══ MERGE HISTORY TAB ═══ */}
      {tab === "history" && (
        <div>
          {loadingHistory ? (
            <div style={styles.empty}>Loading merge history...</div>
          ) : history.length === 0 ? (
            <div style={styles.empty}>No past merges recorded for {entityType}.</div>
          ) : (
            <div style={styles.historyList}>
              {history.map((h) => (
                <div key={h.id} style={styles.historyCard}>
                  <div style={styles.historyTop}>
                    <div>
                      <span style={styles.historyPrimary}>
                        {h.primary_name || h.primary_id}
                      </span>
                      <span style={{ margin: "0 8px", color: "var(--muted, #8A7A66)" }}>← merged</span>
                      <span style={styles.historySecondary}>
                        {h.duplicate_name || h.duplicate_id || h.secondary_id}
                      </span>
                    </div>
                    <span style={styles.historyDate}>
                      {new Date(h.merged_at || h.created_at || "").toLocaleString()}
                    </span>
                  </div>
                  {h.merged_by && (
                    <div style={styles.historyMeta}>Merged by {h.merged_by}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══ COMPARE & MERGE MODAL ═══ */}
      {compareModal && (
        <div style={styles.modalOverlay} onClick={() => setCompareModal(null)}>
          <div style={styles.modalBody} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h2 style={styles.modalTitle}>Compare & Merge {entityType}</h2>
                <p style={styles.modalMeta}>
                  Choose which record to keep as Primary. All history and relationships will transfer.
                </p>
              </div>
              <button style={styles.closeBtn} onClick={() => setCompareModal(null)}>
                ✕
              </button>
            </div>

            {/* Primary Selection Buttons */}
            <div style={styles.primarySelector}>
              <div
                style={{
                  ...styles.primarySide,
                  ...(primaryChoice === "a" ? styles.primarySideSelected : {}),
                }}
                onClick={() => setPrimaryChoice("a")}
              >
                <div style={styles.primaryRadioRow}>
                  <input
                    type="radio"
                    checked={primaryChoice === "a"}
                    onChange={() => setPrimaryChoice("a")}
                  />
                  <strong>Keep Record A as Primary</strong>
                </div>
                <div style={styles.primaryName}>{getItemName(compareModal.item_a)}</div>
                <div style={styles.primarySub}>{getItemSub(compareModal.item_a)}</div>
              </div>

              <div
                style={{
                  ...styles.primarySide,
                  ...(primaryChoice === "b" ? styles.primarySideSelected : {}),
                }}
                onClick={() => setPrimaryChoice("b")}
              >
                <div style={styles.primaryRadioRow}>
                  <input
                    type="radio"
                    checked={primaryChoice === "b"}
                    onChange={() => setPrimaryChoice("b")}
                  />
                  <strong>Keep Record B as Primary</strong>
                </div>
                <div style={styles.primaryName}>{getItemName(compareModal.item_b)}</div>
                <div style={styles.primarySub}>{getItemSub(compareModal.item_b)}</div>
              </div>
            </div>

            {/* Field-by-Field Comparison Table */}
            <div style={styles.tableCard}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Field</th>
                    <th style={styles.th}>Record A</th>
                    <th style={styles.th}>Record B</th>
                  </tr>
                </thead>
                <tbody>
                  {getFieldsList().map((f) => {
                    const valA = compareModal.item_a?.[f.key];
                    const valB = compareModal.item_b?.[f.key];
                    const isDiff = String(valA ?? "") !== String(valB ?? "");
                    const show = (v: any) => {
                      if (typeof v === "boolean") return v ? "Yes" : "No";
                      if (f.isMoney && v) return `$${(v / 100).toFixed(2)}`;
                      if (f.key === "square_updated_at" && v) return new Date(v).toLocaleDateString();
                      return String(v ?? "—");
                    };

                    return (
                      <tr key={f.key} style={{ ...styles.tr, ...(isDiff ? styles.trDiff : {}) }}>
                        <td style={styles.tdField}>
                          {f.label}
                          {isDiff && <span style={styles.diffTag}>Diff</span>}
                        </td>
                        <td style={styles.td}>{show(valA)}</td>
                        <td style={styles.td}>{show(valB)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Square ID choice — only when both records carry a Square link */}
            {entityType === "customers" && squareIdSide && (
              <div style={styles.squareIdCard} role="radiogroup" aria-label="Square customer ID to keep">
                <div style={styles.squareIdTitle}>Square customer ID to keep</div>
                <div style={styles.squareIdHint}>
                  Keep the one Square updated most recently — after a merge in Square only that record stays live.
                  Order and payment history moves with it.
                </div>
                {(["a", "b"] as const).map((side) => {
                  const item = side === "a" ? compareModal.item_a : compareModal.item_b;
                  return (
                    <label key={side} style={styles.squareIdOption}>
                      <input
                        type="radio"
                        name="square-id-side"
                        checked={squareIdSide === side}
                        onChange={() => setSquareIdSide(side)}
                      />
                      <span>
                        Record {side.toUpperCase()}: <strong>{item?.square_customer_id}</strong>
                        {item?.square_updated_at
                          ? ` · updated ${new Date(item.square_updated_at).toLocaleDateString()}`
                          : " · never updated"}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}

            {/* Actions */}
            <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button style={styles.cancelBtn} onClick={() => setCompareModal(null)}>
                Cancel
              </button>
              <button
                style={styles.saveBtn}
                disabled={merging}
                onClick={handleExecuteMerge}
              >
                {merging
                  ? "Merging..."
                  : `Confirm Merge into Record ${primaryChoice.toUpperCase()}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: {
    maxWidth: 960,
    margin: "0 auto",
    padding: "24px 20px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
    flexWrap: "wrap",
    gap: 12,
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 24,
    color: "var(--gold, #F9AF36)",
    margin: "0 0 4px 0",
  },
  subtitle: {
    fontSize: 13,
    color: "var(--muted, #8A7A66)",
    margin: 0,
  },
  entityToggle: {
    display: "flex",
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 6,
    padding: 3,
  },
  entityBtn: {
    background: "transparent",
    border: "none",
    color: "var(--muted, #8A7A66)",
    padding: "6px 14px",
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  entityBtnActive: {
    background: "var(--gold, #F9AF36)",
    color: "#111",
  },
  tabsRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  tabToggle: {
    display: "flex",
    gap: 8,
  },
  tabBtn: {
    background: "transparent",
    border: "1px solid var(--border, #2A1F14)",
    color: "var(--muted, #8A7A66)",
    padding: "6px 14px",
    borderRadius: 5,
    fontSize: 13,
    cursor: "pointer",
  },
  tabBtnActive: {
    borderColor: "var(--gold, #F9AF36)",
    color: "var(--gold, #F9AF36)",
    fontWeight: 700,
    background: "rgba(249, 175, 54, 0.08)",
  },
  refreshBtn: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    color: "var(--text, #F0E6D4)",
    borderRadius: 5,
    padding: "6px 12px",
    fontSize: 12,
    cursor: "pointer",
  },
  errorBanner: {
    background: "rgba(224, 80, 64, 0.15)",
    border: "1px solid rgba(224, 80, 64, 0.4)",
    color: "#ff8b7b",
    padding: "10px 14px",
    borderRadius: 6,
    marginBottom: 16,
    fontSize: 13,
  },
  successBanner: {
    background: "rgba(39, 174, 96, 0.15)",
    border: "1px solid rgba(39, 174, 96, 0.4)",
    color: "#4ade80",
    padding: "10px 14px",
    borderRadius: 6,
    marginBottom: 16,
    fontSize: 13,
  },
  searchCard: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    padding: 16,
    marginBottom: 20,
  },
  searchHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  cancelLink: {
    background: "none",
    border: "none",
    color: "#ff8b7b",
    fontSize: 12,
    cursor: "pointer",
    textDecoration: "underline",
  },
  searchInput: {
    width: "100%",
    background: "var(--bg, #0D0B08)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 5,
    padding: "8px 12px",
    fontSize: 13,
    color: "var(--text, #F0E6D4)",
    boxSizing: "border-box",
  },
  searchStatus: {
    fontSize: 12,
    color: "var(--muted, #8A7A66)",
    marginTop: 8,
    fontStyle: "italic",
  },
  resultsBox: {
    marginTop: 10,
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 6,
    maxHeight: 220,
    overflowY: "auto",
    background: "rgba(0,0,0,0.2)",
  },
  resultItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 12px",
    borderBottom: "1px solid rgba(255,255,255,0.03)",
    cursor: "pointer",
  },
  resultName: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--text, #F0E6D4)",
  },
  resultSub: {
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
    marginTop: 2,
  },
  selectBtn: {
    background: "rgba(249, 175, 54, 0.15)",
    border: "1px solid rgba(249, 175, 54, 0.3)",
    color: "var(--gold, #F9AF36)",
    borderRadius: 4,
    padding: "4px 10px",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
  },
  statsBar: {
    display: "flex",
    gap: 12,
    marginBottom: 16,
  },
  statPill: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 6,
    padding: "8px 14px",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  statVal: {
    fontSize: 16,
    fontWeight: 700,
    color: "var(--gold, #F9AF36)",
  },
  statLabel: {
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
    textTransform: "uppercase",
  },
  pairsGrid: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  pairCard: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    padding: 16,
  },
  pairTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  matchBadge: {
    background: "rgba(249, 175, 54, 0.12)",
    color: "var(--gold, #F9AF36)",
    padding: "3px 8px",
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
  },
  reasonText: {
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
  },
  pairColumns: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    gap: 12,
    alignItems: "center",
    marginBottom: 14,
  },
  pairSide: {
    background: "rgba(0,0,0,0.2)",
    border: "1px solid rgba(255,255,255,0.03)",
    borderRadius: 6,
    padding: "10px 12px",
  },
  sideTag: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    color: "var(--muted, #8A7A66)",
    marginBottom: 4,
  },
  sideName: {
    fontSize: 14,
    fontWeight: 700,
    color: "var(--text, #F0E6D4)",
  },
  sideMeta: {
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
    marginTop: 3,
  },
  vsBadge: {
    fontSize: 12,
    fontWeight: 700,
    color: "var(--muted, #8A7A66)",
    padding: "4px 8px",
    background: "rgba(0,0,0,0.3)",
    borderRadius: 4,
  },
  pairActions: {
    display: "flex",
    justifyContent: "flex-end",
  },
  compareBtn: {
    background: "var(--gold, #F9AF36)",
    color: "#111",
    border: "none",
    borderRadius: 5,
    padding: "7px 14px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  historyList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  historyCard: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 6,
    padding: "12px 16px",
  },
  historyTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  historyPrimary: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--gold, #F9AF36)",
  },
  historySecondary: {
    fontSize: 13,
    color: "var(--text, #F0E6D4)",
    textDecoration: "line-through",
  },
  historyDate: {
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
  },
  historyMeta: {
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
    marginTop: 4,
  },
  empty: {
    textAlign: "center",
    padding: 40,
    color: "var(--muted, #8A7A66)",
    background: "var(--surface, #13100B)",
    borderRadius: 8,
    border: "1px solid var(--border, #2A1F14)",
  },

  // Modal
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.75)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 20,
  },
  modalBody: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 10,
    width: 750,
    maxWidth: "95vw",
    maxHeight: "85vh",
    overflowY: "auto",
    padding: 24,
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 16,
    borderBottom: "1px solid var(--border, #2A1F14)",
    paddingBottom: 12,
  },
  modalTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 18,
    color: "var(--gold, #F9AF36)",
    margin: "0 0 4px 0",
  },
  modalMeta: {
    fontSize: 12,
    color: "var(--muted, #8A7A66)",
    margin: 0,
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: 18,
    color: "var(--muted, #8A7A66)",
    cursor: "pointer",
  },
  primarySelector: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    marginBottom: 16,
  },
  primarySide: {
    background: "rgba(0,0,0,0.2)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    padding: 14,
    cursor: "pointer",
  },
  primarySideSelected: {
    borderColor: "var(--gold, #F9AF36)",
    background: "rgba(249, 175, 54, 0.08)",
  },
  primaryRadioRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 6,
    fontSize: 12,
    color: "var(--gold, #F9AF36)",
  },
  primaryName: {
    fontSize: 14,
    fontWeight: 700,
    color: "var(--text, #F0E6D4)",
  },
  primarySub: {
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
    marginTop: 2,
  },
  tableCard: {
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 6,
    overflow: "hidden",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 12,
  },
  th: {
    background: "rgba(0,0,0,0.4)",
    textAlign: "left",
    padding: "8px 12px",
    color: "var(--gold, #F9AF36)",
    fontSize: 11,
    textTransform: "uppercase",
    borderBottom: "1px solid var(--border, #2A1F14)",
  },
  tr: {
    borderBottom: "1px solid rgba(255,255,255,0.03)",
  },
  trDiff: {
    background: "rgba(249, 175, 54, 0.04)",
  },
  tdField: {
    padding: "8px 12px",
    color: "var(--muted, #8A7A66)",
    fontWeight: 600,
    width: "25%",
  },
  diffTag: {
    fontSize: 9,
    background: "rgba(249, 175, 54, 0.2)",
    color: "var(--gold, #F9AF36)",
    padding: "1px 4px",
    borderRadius: 3,
    marginLeft: 6,
    textTransform: "uppercase",
  },
  td: {
    padding: "8px 12px",
    color: "var(--text, #F0E6D4)",
    width: "37.5%",
  },
  squareIdCard: {
    marginTop: 14,
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 6,
    padding: "12px 14px",
    background: "rgba(0,0,0,0.2)",
  },
  squareIdTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: "var(--gold, #F9AF36)",
    textTransform: "uppercase",
    marginBottom: 4,
  },
  squareIdHint: {
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
    marginBottom: 8,
  },
  squareIdOption: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    color: "var(--text, #F0E6D4)",
    padding: "6px 0",
    minHeight: 44,
    cursor: "pointer",
  },
  cancelBtn: {
    background: "transparent",
    color: "var(--muted, #8A7A66)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 5,
    padding: "8px 16px",
    fontSize: 13,
    cursor: "pointer",
  },
  saveBtn: {
    background: "var(--gold, #F9AF36)",
    color: "#111",
    border: "none",
    borderRadius: 5,
    padding: "8px 18px",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
  },
};
